'use client';

import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@kit/ui/card';

import type { BookingCourtRow } from '../_lib/server/booking-page.loader';
import { selectCourtLabel } from '../_lib/format-court-label';
import { ImageCarousel } from '../../_components/image-carousel';

interface SingleCourtHeroProps {
  /** The venue's only court for the selected sport, already resolved by the
   *  caller. The caller owns the schools + exactly-one-court + has-images test
   *  so the very same condition can suppress the redundant "Select Court"
   *  picker further down the form. */
  court: BookingCourtRow;
}

/**
 * Shows the venue's court up front when the selected sport has exactly ONE
 * bookable court with images — the single-hall school case, where the usual
 * "Select Court" card is a pointless one-option radio.
 *
 * Deliberately presentational: renders no radio and dispatches nothing. The
 * court is auto-selected by the reducer's SELECT_TIME branch (which already
 * picks the only court whenever `courts.length === 1`), so this cannot desync
 * from the form's real selection state.
 */
export function SingleCourtHero({ court }: SingleCourtHeroProps) {
  const { i18n } = useTranslation();

  const images = court.image_urls ?? [];
  const label = selectCourtLabel(court, i18n.language);

  return (
    <Card className="overflow-hidden">
      {/* The carousel is 4:3, which renders enormous in the wide desktop
          column. Cap the height from md up and let object-cover crop; mobile
          keeps the natural 4:3 ratio, where it already looked right. */}
      <ImageCarousel
        images={images}
        alt={label.name}
        className="md:aspect-auto md:h-64 lg:h-72"
      />
      <CardContent className="p-3 text-center">
        <p className="text-base font-bold md:text-lg">{label.name}</p>
        {label.type && (
          <p className="text-muted-foreground mt-1 text-xs font-medium tracking-wide uppercase">
            {label.type}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
